/**
 * Modern Bold · <mb-product-tabs> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * PDP details tabs (Description / Video / Specs / Care / Shipping /
 * comparison). Vertical rail on desktop, horizontal scroll strip under
 * 750px — the CSS owns that switch; this element only mirrors it into
 * aria-orientation.
 *
 * Progressive enhancement: the Liquid snippet ships inactive panels with
 * [hidden] and the default tab with .is-active, so the band paints in its
 * final state before this script runs (no flash of stacked panels). A
 * per-instance <noscript> style in the snippet restores the stacked
 * reading view when JS is off. Tab ARIA is applied here, not server-side —
 * advertising tablist semantics the keyboard can't operate (JS
 * blocked/failed) would be worse than none.
 *
 * Keyboard (WAI-ARIA APG tabs, automatic activation + roving tabindex):
 *   ArrowUp/ArrowLeft    previous tab (wraps)
 *   ArrowDown/ArrowRight next tab (wraps)
 *   Home / End           first / last tab
 *   Tab                  leaves the tablist to the active panel/content —
 *                        inactive tabs stay reachable via the arrow keys.
 *
 * DOM contract (rendered by snippets/modern-product-tabs.liquid):
 *   <mb-product-tabs data-default-tab="description|video|specs|care|ship|comparison">
 *     [data-mb-tabs-rail]           tablist container
 *       [data-mb-tab="<key>"] × N   tab buttons (id required)
 *     [data-mb-panel="<key>"] × N   panels (id required, same keys)
 */
if (!customElements.get('mb-product-tabs')) {
  class MBProductTabs extends HTMLElement {
    #cleanups = [];
    #rail = null;
    #tabs = [];
    #panels = [];
    #current = -1;
    #mq = null;

    connectedCallback() {
      this.#rail = this.querySelector('[data-mb-tabs-rail]');
      const buttons = Array.from(this.querySelectorAll('[data-mb-tab]'));
      // Keep only tabs whose panel actually rendered (panels are
      // metafield-gated in Liquid, so the pair check is cheap insurance).
      this.#tabs = buttons.filter((tab) => this.querySelector(`[data-mb-panel="${tab.dataset.mbTab}"]`));
      this.#panels = this.#tabs.map((tab) => this.querySelector(`[data-mb-panel="${tab.dataset.mbTab}"]`));
      if (!this.#rail || this.#tabs.length === 0) return;

      this.#rail.setAttribute('role', 'tablist');

      this.#tabs.forEach((tab, i) => {
        const panel = this.#panels[i];
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        // Panels with no focusable child get tabindex=0 so keyboard users
        // can reach and scroll the content (APG tabpanel guidance).
        if (!panel.querySelector('a[href], button, iframe, input, select, textarea, [tabindex]')) {
          panel.setAttribute('tabindex', '0');
        }
        const onClick = () => this.#select(i, { focus: false });
        tab.addEventListener('click', onClick);
        this.#cleanups.push(() => tab.removeEventListener('click', onClick));
      });

      const onKeydown = (event) => this.#onKeydown(event);
      this.#rail.addEventListener('keydown', onKeydown);
      this.#cleanups.push(() => this.#rail.removeEventListener('keydown', onKeydown));

      this.#mq = window.matchMedia('(min-width: 750px)');
      const onMqChange = () => this.#syncOrientation();
      this.#mq.addEventListener('change', onMqChange);
      this.#cleanups.push(() => this.#mq.removeEventListener('change', onMqChange));
      this.#syncOrientation();

      const wanted = this.dataset.defaultTab;
      const start = this.#tabs.findIndex((tab) => tab.dataset.mbTab === wanted);
      // keepVisible: false — the initial paint must never move the page.
      this.#select(start === -1 ? 0 : start, { focus: false, keepVisible: false });
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    #syncOrientation() {
      this.#rail.setAttribute('aria-orientation', this.#mq.matches ? 'vertical' : 'horizontal');
    }

    #onKeydown(event) {
      const last = this.#tabs.length - 1;
      let next;
      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
          next = this.#current === 0 ? last : this.#current - 1;
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          next = this.#current === last ? 0 : this.#current + 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.#select(next);
    }

    #select(index, { focus = true, keepVisible = true } = {}) {
      if (index !== this.#current) {
        this.#current = index;
        this.#tabs.forEach((tab, i) => {
          const active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          // Roving tabindex (APG): one Tab stop for the whole tablist,
          // arrows reach the inactive tabs.
          tab.tabIndex = active ? 0 : -1;
          tab.classList.toggle('is-active', active);
          this.#panels[i].hidden = !active;
        });
      }
      if (focus) this.#tabs[index].focus();
      if (keepVisible) this.#keepTabVisible(this.#tabs[index]);
    }

    /**
     * Scroll the mobile strip so the active tab is fully visible.
     *
     * Deliberately NOT Element.scrollIntoView: that walks every scrollable
     * ancestor including the document, so on load it yanked the whole page
     * down to the tab band. This only ever moves the rail, and only on the
     * horizontal axis — the page scroll position is never touched.
     */
    #keepTabVisible(tab) {
      const rail = this.#rail;
      // Desktop rail is a vertical column with no overflow: nothing to do.
      if (rail.scrollWidth <= rail.clientWidth) return;

      const t = tab.getBoundingClientRect();
      const r = rail.getBoundingClientRect();
      if (t.left >= r.left && t.right <= r.right) return;

      const delta = t.left < r.left ? t.left - r.left : t.right - r.right;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      rail.scrollBy({ left: delta, behavior: reduced ? 'auto' : 'smooth' });
    }
  }

  customElements.define('mb-product-tabs', MBProductTabs);
}
